<template>
    <Modal :show="show" :max-width="'7xl'" class="h-[90vh] overflow-hidden" @close="close">
        <div class="flex flex-col w-full h-full gap-px">
            <div
                class="grid items-center grow-0 grid-cols-12 px-2 py-2 bg-gray-100 dark:bg-neutral-950 media-picker__bar"
                :class="[{ 'animate-pulse': loading.galleries }]"
            >
                <div class="col-span-2">
                    <AdminText
                        v-model="search"
                        type="text"
                        :placeholder="lang('media::galleries.gallery.search_placeholder')"
                        @input="deferredSearch"
                    />
                </div>
                <div class="col-span-8"></div>
                <div class="flex items-center justify-end col-span-2 space-x-1 text-sm">
                    <Button color="default" class="py-2" :disabled="!canPreviousPage" @click="previousPage">
                        <icon icon="hero/outline/chevron-left" class="size-3"></icon>
                    </Button>
                    <Button color="default" class="py-2" :disabled="!canNextPage" @click="nextPage">
                        <icon icon="hero/outline/chevron-right" class="size-3"></icon>
                    </Button>
                </div>
            </div>
            <main class="grid flex-auto grid-cols-12 gap-px overflow-hidden">
                <div
                    class="h-full overflow-y-auto select-none"
                    :class="{
                        'col-span-12': !showSidebar,
                        'col-span-9': showSidebar,
                    }"
                >
                    <ul
                        v-if="!loading.galleries && galleries && galleries.length > 0"
                        class="grid grid-cols-1 gap-px pt-px divide-y divide-neutral-200 dark:divide-neutral-700"
                        :class="[{ 'animate-pulse': loading.galleries }]"
                    >
                        <li
                            v-for="(item, index) in galleries"
                            :key="item.id"
                            class="relative flex bg-white cursor-pointer group hover:bg-neutral-50 dark:bg-neutral-900 dark:hover:bg-neutral-950"
                            :class="[{ 'animate-pulse': loading.gallery === item.id }]"
                            @click="openGallery(item)"
                        >
                            <div class="flex justify-center grow-0 px-4 py-10 lg:px-8">
                                <icon icon="hero/outline/folder" class="w-6 h-6"></icon>
                            </div>
                            <div class="flex flex-col flex-auto gap-2 py-4">
                                <div class="font-bold">
                                    {{ item.name }}
                                    <span class="text-xs font-normal text-neutral-600 dark:text-neutral-500">
                                        {{
                                            lang('media::galleries.gallery.update_info', {
                                                created_at: dateHumanReadable(item.created_at),
                                                updated_at: dateHumanReadable(item.updated_at),
                                            })
                                        }}
                                    </span>
                                </div>
                                <div
                                    v-if="item.preview && item.preview.length > 0"
                                    class="grid grid-cols-4 w-fit media-picker__grid"
                                >
                                    <div v-for="image in item.preview" :key="image.id">
                                        <img
                                            :src="image.thumbnail_url"
                                            :alt="image.alt"
                                            class="object-cover w-10 h-10"
                                        />
                                    </div>
                                    <div v-if="item.plus > 0">
                                        <div
                                            class="flex items-center justify-center w-10 h-10 text-sm font-bold bg-neutral-100 dark:bg-neutral-950 text-neutral-500 dark:text-neutral-600"
                                        >
                                            <span>+{{ item.plus }}</span>
                                        </div>
                                    </div>
                                </div>
                                <div
                                    v-if="openedGallery === item.id && galleryImages && galleryImages.length > 0"
                                    class="flex flex-wrap gap-px"
                                >
                                    <div
                                        v-for="image in galleryImages"
                                        :key="image.id"
                                        class="relative cursor-pointer size-20 group/image"
                                        @click.stop="selectMedia(image, index, $event)"
                                    >
                                        <img
                                            :src="image.thumbnail_url"
                                            :alt="image.alt"
                                            class="object-cover w-full h-full"
                                        />
                                        <div
                                            class="absolute inset-0 z-20 border-4 group-hover/image:border-blue-300 media-picker__ring"
                                            :class="{
                                                'border-blue-600 is-selected': isSelected(image.id),
                                                'border-transparent': !isSelected(image.id),
                                            }"
                                        ></div>
                                    </div>
                                </div>
                            </div>
                        </li>
                    </ul>
                    <div
                        v-else-if="!loading.galleries && galleries && galleries.length === 0"
                        class="flex items-center justify-center h-full"
                    >
                        <IllustratedMessage>
                            <template #image>
                                <icon icon="hero/outline/search" class="w-12 h-12"></icon>
                            </template>
                            <template #title>{{ lang('media::galleries.gallery.no_results') }} </template>
                            {{ lang('media::galleries.gallery.no_results_description') }}
                        </IllustratedMessage>
                    </div>
                    <div v-else class="flex items-center justify-center h-full">
                        <IllustratedMessage>
                            <template #image>
                                <icon icon="hero/outline/refresh" class="w-12 h-12 animate-spin"></icon>
                            </template>
                            <template #title>{{ lang('media::galleries.gallery.loading') }} </template>
                            {{ lang('media::galleries.gallery.loading_description') }}
                        </IllustratedMessage>
                    </div>
                </div>
                <div v-if="showSidebar" class="flex flex-col h-full col-span-3">
                    <GalleryDetail :item="detail" @close="closeDetail" />
                </div>
            </main>
            <div class="grid items-center grow-0 grid-cols-12 px-2 py-2 bg-gray-100 dark:bg-neutral-950 media-picker__footer">
                <div class="flex justify-end col-span-2 col-start-11 space-x-1">
                    <Button color="default" @click="close">{{ lang('general.close') }}</Button>
                    <Button color="primary" @click="submit">{{ lang('media::media.actions.select') }}</Button>
                </div>
            </div>
        </div>
    </Modal>
</template>

<script setup lang="ts">
import { ref, watch, computed } from 'vue';
import type { SavedFile } from '@src/Common/Components/Fields/types';
import { dateHumanReadable } from '@src/Common/Helpers/dates';
import GalleryDetail from './GalleryDetail.vue';

type FileValue = File | FileList | SavedFile | (File | SavedFile)[] | null;

const emit = defineEmits<{
    (event: 'close'): void;
    (event: 'select', item: any): void;
    (event: 'newSelected', item: any): void;
    (event: 'deselected', item: any): void;
    (event: 'detail', item: any): void;
}>();

interface Props {
    show?: boolean;
    selected?: FileValue;
    multiple?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
    show: true,
    selected: null,
    multiple: false,
});

const show = ref(props.show);

const pagination = ref({
    total: 0,
    page: 1,
    take: 60,
    last_page: 1,
});

const selected = ref(props.selected);
const newSelected = ref([]);
const deselected = ref([]);

const galleries = ref([]);

const search = ref('');

const close = () => {
    show.value = false;

    emit('close');
};

const submit = () => {
    emit('select', selected.value);
    emit('newSelected', newSelected.value);
    emit('deselected', deselected.value);

    close();
};

const loading = ref({
    galleries: false,
    gallery: false,
    info: false,
});

const loadGalleries = () => {
    loading.value.galleries = true;
    axios
        .get(route('media/galleries.admin.api.galleries'), {
            params: {
                search: search.value,
                page: pagination.value.page,
            },
        })
        .then(({ data }) => {
            galleries.value = data.results;
            pagination.value = {
                total: data.pagination.total,
                page: data.pagination.page,
                take: data.pagination.take,
                last_page: data.pagination.last_page,
            };
            loading.value.galleries = false;
        });
};

const canPreviousPage = computed(() => pagination.value.page > 1);
const canNextPage = computed(() => pagination.value.page < pagination.value.last_page);

const previousPage = () => {
    if (canPreviousPage.value) {
        pagination.value.page -= 1;
        loadGalleries();
    }
};

const nextPage = () => {
    if (canNextPage.value) {
        pagination.value.page += 1;
        loadGalleries();
    }
};

let searchTimeout = null;

const deferredSearch = () => {
    if (searchTimeout) {
        clearTimeout(searchTimeout);
    }

    searchTimeout = setTimeout(() => {
        pagination.value.page = 1;
        loadGalleries();
    }, 300);
};

/**
 * Sidebar
 */
const detail = ref(null);
const showSidebar = ref(false);

const openDetail = () => {
    showSidebar.value = true;
};

const closeDetail = () => {
    showSidebar.value = false;
};

const lastSelectedIndex = ref<number | null>(null);

/**
 * @param {object} item - media item
 * @param {number} index - index of media item
 * @param {MouseEvent} event - click event to figure out (metaKey, ctrlKey, shiftKey)
 */
const selectMedia = (item, index, event) => {
    loading.value.info = true;

    axios.get(route('media.admin.api.media.info', { id: item.id })).then(({ data }) => {
        emit('detail', data);

        loading.value.info = false;

        // Single select
        if (!props.multiple) {
            selected.value = data;
            newSelected.value = [data];
            detail.value = data;
            openDetail();
            loading.value.info = false;
            return;
        }

        // Multiple select
        const isShift = event.shiftKey;
        const isCmd = event.metaKey || event.ctrlKey;

        if (isShift && lastSelectedIndex.value !== null) {
            const start = Math.min(lastSelectedIndex.value, index);
            const end = Math.max(lastSelectedIndex.value, index);

            const rangeItems = galleryImages.value.slice(start, end + 1);

            const alreadySelected = Array.isArray(selected.value) ? selected.value : [];

            const newSelection = [...alreadySelected];
            rangeItems.forEach((it) => {
                if (!newSelection.some((sel) => sel.id === it.id)) {
                    newSelection.push(it);
                }
            });

            selected.value = newSelection;

            const newlyAdded = rangeItems.filter((r) => !alreadySelected.some((sel) => sel.id === r.id));
            newSelected.value = [...newSelected.value, ...newlyAdded];

            lastSelectedIndex.value = index;
        } else if (isCmd) {
            if (isSelected(item.id)) {
                selected.value = selected.value.filter(
                    (selectedItem) => parseInt(selectedItem.id) !== parseInt(item.id),
                );
                deselected.value = [...deselected.value, data];
            } else {
                selected.value = [...selected.value, data];
                newSelected.value = [...newSelected.value, data];

                lastSelectedIndex.value = index;
            }
        } else {
            selected.value = [data];
            newSelected.value = [data];
            lastSelectedIndex.value = index;
        }

        detail.value = data;
        openDetail();

        loading.value.info = false;
    });
};

const isSelected = (id) => {
    if (props.multiple) {
        return selected.value?.some((item) => (item ? parseInt(item.id) === id : false));
    }

    return parseInt(selected.value?.id) === id;
};

/**
 * Gallery actions
 */
const openedGallery = ref(null);
const galleryImages = ref([]);

const openGallery = (item) => {
    openedGallery.value = null;

    loading.value.gallery = item.id;

    axios.get(route('media/galleries.admin.api.gallery', { id: item.id })).then(({ data }) => {
        galleryImages.value = data.results;

        loading.value.gallery = false;

        openedGallery.value = item.id;
    });
};

watch(
    () => show.value,
    async (value) => {
        if (value) {
            loadGalleries();
        }
    },
    { immediate: true },
);
</script>
